import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { AddOnFuelSurchargeRate_Read, AddOnRateType } from '@treadinc/horizon-api-spec';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useEffect, useState } from 'react';

import useAddOns from '~hooks/useAddOns/useAddOns';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { rateTypeTextByAddOnRateType } from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { EstimateCard } from '../shared-components';
import { AddOnAssignmentSchema } from './AutomaticAddOns';

export default function AddOnAssignmentCard({
  addOnAssignments,
  orderDate,
}: {
  addOnAssignments: AddOnAssignmentSchema[];
  orderDate?: string | Dayjs;
}) {
  const filteredAddOnAssignments = addOnAssignments.filter(
    (addOnAssignment) => !addOnAssignment.isDeleted,
  );

  if (filteredAddOnAssignments.length === 0) {
    return null;
  }

  return (
    <EstimateCard title={t('common.add_ons')}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mt: 1,
        }}
      >
        {filteredAddOnAssignments.map((addOnAssignment) => (
          <Box
            key={addOnAssignment.addOn?.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <AddOnAssignmentDetails
              addOnAssignment={addOnAssignment}
              orderDate={orderDate}
            />
          </Box>
        ))}
      </Box>
    </EstimateCard>
  );
}

const FuelSurchargeRateDisplay = ({
  addOnId,
  orderDate,
}: {
  addOnId?: string;
  orderDate?: string | Dayjs;
}) => {
  const { getFuelSurchargeRate } = useAddOns();
  const { currencyFormatter } = useCompanyCurrency();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [rate, setRate] = useState<AddOnFuelSurchargeRate_Read | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !addOnId) return;
    const controller = new AbortController();
    const fetchRate = async () => {
      setIsLoading(true);
      const dateStr = orderDate
        ? typeof orderDate === 'string'
          ? orderDate
          : orderDate.format('YYYY-MM-DD')
        : undefined;
      try {
        const result = await getFuelSurchargeRate(
          companyId,
          addOnId,
          dateStr,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        setRate(result);
        setIsLoading(false);
      } catch (error) {
        if (controller.signal.aborted) return;
        throw error;
      }
    };
    void fetchRate();
    return () => controller.abort();
  }, [companyId, addOnId, orderDate]);

  if (isLoading) {
    return <CircularProgress size={12} sx={{ ml: 0.5, mr: 0.5 }} />;
  }
  if (!isNil(rate?.surcharge_percentage)) {
    return <>{`${rate.surcharge_percentage}%`}</>;
  }
  if (!isNil(rate?.surcharge)) {
    return <>{currencyFormatter(Number(rate.surcharge))}</>;
  }
  return <>-</>;
};

const AddOnAssignmentDetails = ({
  addOnAssignment,
  orderDate,
}: {
  addOnAssignment: AddOnAssignmentSchema;
  orderDate?: string | Dayjs;
}) => {
  const displayRateType = (rateType: AddOnRateType | null) =>
    rateType ? rateTypeTextByAddOnRateType[rateType] : '';

  const { currencyFormatter } = useCompanyCurrency();
  const isFuelSurcharge =
    addOnAssignment.addOn?.rateType === AddOnRateType.FUEL_SURCHARGE;

  return (
    <Box>
      <Typography>{addOnAssignment.addOn?.name}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {isFuelSurcharge ? (
          <>
            {t('administration.rates.add_ons.fuel_surcharge_schedule')}
            {`: ${addOnAssignment.addOn?.fuelSurchargeSchedule?.name}`}
            {' • '}
            {t('administration.rates.invoice_rate')}
            {': '}
            <FuelSurchargeRateDisplay
              addOnId={
                addOnAssignment.addOn?.addOnId ?? addOnAssignment.addOn?.id ?? undefined
              }
              orderDate={orderDate}
            />
            {' • '}
          </>
        ) : (
          <>
            {' '}
            {!isNil(addOnAssignment.addOn?.costRateValue) && (
              <>
                {t('administration.rates.add_ons.cost_rate')}
                {`: ${currencyFormatter(addOnAssignment.addOn.costRateValue)}`}
                {' • '}
              </>
            )}
            {t('administration.rates.invoice_rate')}
            {addOnAssignment.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
              ? `: ${addOnAssignment.addOn?.rateValue}%`
              : `: ${currencyFormatter(addOnAssignment.addOn?.rateValue ?? 0)}`}
            {' • '}
            {addOnAssignment.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
            addOnAssignment.addOn?.rateType === AddOnRateType.PER_LOAD
              ? `${t('administration.rates.rate_quantity_label')}: ${addOnAssignment.addOn?.quantity} • `
              : ''}
          </>
        )}
        {t('administration.rates.rate_type')}:{' '}
        {displayRateType((addOnAssignment.addOn?.rateType as AddOnRateType) ?? null)}
      </Typography>
    </Box>
  );
};
